// A vendor's contract history (aldoo-na store/payouts/contracts ofOrg, the
// overview's contract.list): every contract sent, signed or uploaded, newest
// first — its terms, dates, status and why it was made — each opened in the
// PDF viewer (the signed file once signed), downloaded from there. The vendor sees
// their own (never drafts or withdrawn ones); the admin sees the withdrawn ones
// too, what each change did to the sales, and uploads a contract signed before
// (on paper, elsewhere) with its terms and dates (UploadSignedDialog), which
// then settles the sales of its period.
import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import usePdfModal from "./usePdfModal"
import DropZone from "aldoo-ra/DropZone"
import { ArrowDownTrayIcon, ChevronDownIcon, DocumentArrowUpIcon, DocumentTextIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { showSuccessToast, showErrorToast } from "aldoo-ra/Toast"
import { Field, NumberInput, TextInput, TextArea, Toggle, DateInput, Chip, dateOnly, money } from "../AdminShared/ui"
import { PayoutsAdmin, PayoutsAdminUpload } from "../AdminShared/api"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { PAYOUTS } from "../Vendor/texts/bundles"
import ContractPdf from "./ContractPdf"
import { downloadPdf } from "./pdf"
import { scheduleText } from "./schedule"
import ActionsMenu from "./ActionsMenu"

// the server's limit (store/payouts/contracts MAX_UPLOAD_BYTES)
export const MAX_PDF_BYTES = 15 * 1024 * 1024

// what the server answers an upload it refused with, as the admin reads it
export const UPLOAD_ERRORS = {
  file_required: "Choose a PDF file",
  file_too_large: "The PDF is larger than 15 MB",
  file_not_pdf: "The file is not a PDF",
  pdf_invalid: "The PDF could not be read",
  pdf_encrypted: "The PDF is password-protected — upload one without a password",
  signed_at_invalid: "The signing date is missing or in the future",
  effective_from_invalid: "The start date is not valid",
  effective_to_invalid: "The end date must be after the start",
  effective_from_taken: "Another contract starts on that day",
  vendor_percent_invalid: "The vendor's share must be 0–100, a whole number or one decimal",
  hold_days_invalid: "Hold days must be a whole number, 0 or more",
  payout_day_invalid: "The payout day must be 1–28",
  platform_signature_missing: "Set Aldoo's signature in Admin → Vendors → Contracts first",
  reason_required: "Say why the contract changes",
  changes_required: "List what changes in the contract",
}

const sizeOf = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`)

/** A contract PDF picked (aldoo-ra/DropZone): dropped or chosen, a PDF of at most 15 MB. Admin. */
export function PdfPicker({ file, onChange, "data-testid": testId }) {
  const pick = (accepted, rejected) => {
    if (rejected?.length) return showErrorToast("Only a PDF file can be uploaded")
    const picked = accepted?.[0]
    if (!picked) return
    if (picked.size > MAX_PDF_BYTES) return showErrorToast(UPLOAD_ERRORS.file_too_large)
    onChange(picked)
  }
  if (file)
    return (
      <div className="flex items-center gap-3 rounded-md border border-admin_primary bg-admin_primary/5 px-3 py-2 text-sm" data-testid={testId}>
        <DocumentTextIcon className="w-6 h-6 shrink-0 text-admin_primary" />
        <span className="flex-1 min-w-0 truncate">{file.name}</span>
        <span className="text-xs text-admin_text_secondary tabular-nums">{sizeOf(file.size)}</span>
        <button type="button" className="p-1 rounded text-admin_text_secondary hover:text-admin_error" onClick={() => onChange(null)} title="Remove" aria-label="Remove the file">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    )
  return (
    <DropZone
      accept={{ "application/pdf": [".pdf"] }}
      maxFiles={1}
      onDrop={pick}
      onRender={({ isDragging, handleClick, handleDragEnter, handleDragOver, handleDragLeave, handleDrop }) => (
        <div
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex flex-col items-center gap-1 rounded-md border-2 border-dashed px-4 py-5 text-center text-sm cursor-pointer transition-colors ${isDragging ? "border-admin_primary bg-admin_primary/10" : "border-admin_bordered bg-admin_canvas hover:border-admin_primary hover:bg-admin_primary/5"}`}
          data-testid={testId}
        >
          <DocumentArrowUpIcon className="w-7 h-7 text-admin_text_secondary" />
          <span className="font-medium">Drop the contract PDF here or click to choose</span>
          <span className="text-xs text-admin_text_secondary">PDF, up to 15 MB</span>
        </div>
      )}
    />
  )
}

const round1 = (n) => Math.round(n * 10) / 10
const shareOf = (c) => Math.round((100 - Number(c.commissionPercent)) * 100) / 100
/** A contract's number as its document names it: the last 8 of its id and its version. */
export const numberOf = (c) => `${String(c._id).slice(-8).toUpperCase()}/${c.version}`

/**
 * The admin uploads a contract signed before — on paper, elsewhere — as its
 * signed PDF, with the terms it set and its dates: the vendor's history gets
 * it, and the sales of its period are settled at its terms (unpaid ones at
 * once; paid ones stay as they were paid).
 */
export function UploadSignedDialog({ orgId, open, onClose, onDone }) {
  const blank = { vendorPercent: null, holdDays: null, payoutDay: null, deliveryToVendor: true, signedAt: "", effectiveFrom: "", effectiveTo: "", title: "", notes: "" }
  const [form, setForm] = useState(blank)
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  useEffect(() => {
    if (!open) return
    setForm(blank)
    setFile(null)
    PayoutsAdmin("defaults")
      .then((res) => res?.defaults && setForm((f) => ({ ...f, vendorPercent: res.defaults.vendorPercent, holdDays: res.defaults.holdDays, payoutDay: res.defaults.payoutDay })))
      .catch(() => {})
  }, [open])

  const share = Number(form.vendorPercent)
  const shareValid = form.vendorPercent !== null && Number.isFinite(share) && share >= 0 && share <= 100 && round1(share) === share
  const daysValid = Number.isInteger(Number(form.holdDays)) && Number(form.holdDays) >= 0
  const dayValid = Number.isInteger(Number(form.payoutDay)) && Number(form.payoutDay) >= 1 && Number(form.payoutDay) <= 28
  const today = new Date().toISOString().slice(0, 10)
  const signedValid = !!form.signedAt && form.signedAt <= today
  const start = form.effectiveFrom || form.signedAt
  const endValid = !form.effectiveTo || (!!start && form.effectiveTo > start)
  const valid = !!file && shareValid && daysValid && dayValid && signedValid && endValid

  const submit = async () => {
    setBusy(true)
    const { vendorPercent, holdDays, payoutDay, deliveryToVendor, signedAt, effectiveFrom, effectiveTo, title, notes } = form
    const res = await PayoutsAdminUpload("contract_upload_signed", file, { orgId, vendorPercent, holdDays, payoutDay, deliveryToVendor, signedAt, effectiveFrom: effectiveFrom || undefined, effectiveTo: effectiveTo || undefined, title: title.trim(), notes: notes.trim() }).catch((e) => ({ error: e?.response?.data?.error || e?.message }))
    setBusy(false)
    if (!res || res.error) return showErrorToast("The contract could not be uploaded", UPLOAD_ERRORS[res?.error] || res?.error)
    const r = res.result
    showSuccessToast(
      `Contract uploaded (v${res.contract?.version}, ${res.contract?.status === "signed" ? "in force" : "superseded"})`,
      r ? `${r.orders} orders re-settled, ${r.updated} unpaid rows changed; ${r.paidRows} paid rows in its period left as paid` : undefined
    )
    onDone?.(res)
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => !busy && onClose?.()}
      title="Upload a signed contract"
      variant="admin"
      closeOnEscape={!busy}
      styleOverrides={{ container: "w-full max-w-[680px] max-h-full md:max-h-[calc(100vh-2rem)] bg-admin_paper md:rounded-lg border border-admin_bordered shadow-lg overflow-hidden" }}
    >
      <div className="flex flex-col gap-3" data-testid="contract-upload-dialog">
        <div className="text-xs text-admin_text_secondary">
          A contract the vendor signed before (on paper or elsewhere). It joins the vendor's contract history, and the sales made from its start until the next contract are settled at the terms below — unpaid
          ones at once; sales already paid keep what was paid.
        </div>
        <Field label="Signed contract (PDF)">
          <PdfPicker file={file} onChange={setFile} data-testid="upload-file" />
        </Field>
        <Field label="Title (optional)">
          <TextInput value={form.title} onChange={(v) => set("title", v)} placeholder="e.g. Paper contract 2024" maxLength={200} />
        </Field>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Vendor's share %" error={form.vendorPercent !== null && !shareValid ? "0–100, one decimal at most" : null} hint={shareValid ? `Aldoo commission ${round1(100 - share)}%` : null}>
            <NumberInput value={form.vendorPercent} onChange={(v) => set("vendorPercent", v)} allowEmpty step="0.1" min="0" max="100" data-testid="upload-share" />
          </Field>
          <Field label="Hold days before payout" error={form.holdDays !== null && !daysValid ? "0 or more" : null}>
            <NumberInput value={form.holdDays} onChange={(v) => set("holdDays", v)} min="0" />
          </Field>
          <Field label="Payout day of month (1–28)" error={form.payoutDay !== null && !dayValid ? "1–28" : null}>
            <NumberInput value={form.payoutDay} onChange={(v) => set("payoutDay", v)} min="1" max="28" />
          </Field>
        </div>
        <Toggle checked={form.deliveryToVendor} onChange={(v) => set("deliveryToVendor", v)} label="Delivery fee goes to the vendor in full (no commission)" />
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Signed on" error={form.signedAt && !signedValid ? "Not in the future" : null}>
            <DateInput value={form.signedAt} onChange={(v) => set("signedAt", v)} placeholder="Select date" minYear={2000} data-testid="upload-signed-at" />
          </Field>
          <Field label="In force from" hint="Empty: the day it was signed">
            <DateInput value={form.effectiveFrom} onChange={(v) => set("effectiveFrom", v)} placeholder={form.signedAt ? form.signedAt.split("-").reverse().join(".") : "Select date"} minYear={2000} />
          </Field>
          <Field label="In force until (optional)" error={!endValid ? "After the start" : null} hint="Empty: until the next contract">
            <DateInput value={form.effectiveTo} onChange={(v) => set("effectiveTo", v)} placeholder="Open-ended" minYear={2000} />
          </Field>
        </div>
        <Field label="Notes (optional, shown to the vendor)">
          <TextArea value={form.notes} onChange={(v) => set("notes", v)} rows={2} />
        </Field>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <Button variant="admin_primary" disabled={busy || !valid} onClick={submit} data-testid="upload-submit">
            {busy ? "Uploading…" : "Upload contract"}
          </Button>
          <Button variant="admin_secondary" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

const SOURCE = { generated: "Generated in Aldoo", upload: "Uploaded", upload_for_signature: "Uploaded for signature" }
const STATUS = {
  sent: ["Waiting for signature", "bg-admin_warning/10 text-admin_warning"],
  signed: ["In force", "bg-admin_success/10 text-admin_success"],
  superseded: ["Superseded", "bg-admin_neutral_1 text-admin_text_secondary"],
  corrected: ["Corrected", "bg-admin_neutral_1 text-admin_text_secondary"],
  void: ["Withdrawn", "bg-admin_error/10 text-admin_error"],
}
const REASON = { terms: "New terms", correction: "Correction" }
const statusOf = (c) => (c.status === "superseded" && c.correctedBy ? "corrected" : c.status)
// the file a contract is read in: the signed one once there is one
const whichOf = (c) => (c.hasSigned ? "signed" : c.hasUnsigned ? "unsigned" : null)

/** One contract of the history. */
function HistoryRow({ contract: c, admin, onOpen, locale, actions, signerNote }) {
  const t = usePortalTexts(PAYOUTS)
  const [label, chip] = STATUS[statusOf(c)] || [c.status, "bg-admin_neutral_1"]
  const which = whichOf(c)
  const from = c.effectiveFrom || c.signedAt
  // folded to its heading, as the portal's other boxes: the chevron opens its terms and dates
  const [open, setOpen] = useState(false)
  return (
    <li className="flex flex-col gap-1 py-3" data-testid="contract-history-row" data-version={c.version}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex items-center gap-2 text-left" data-testid="contract-history-toggle">
          <ChevronDownIcon className={`w-5 h-5 shrink-0 text-admin_text_secondary transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="font-semibold">{t("Contract No. %s", numberOf(c))}</span>
        </button>
        <Chip className={chip}>{t(label)}</Chip>
        {/* where it came from, only when it was not made here (an uploaded one) */}
        {c.source && c.source !== "generated" && SOURCE[c.source] && <Chip className="bg-admin_neutral_1 text-admin_text_secondary">{t(SOURCE[c.source])}</Chip>}
        {c.reason && <Chip className={c.reason === "correction" ? "bg-admin_warning/10 text-admin_warning" : "bg-admin_neutral_1 text-admin_text_secondary"}>{t(REASON[c.reason])}</Chip>}
        {/* beside the name (not pushed to the far edge); downloaded from the viewer's own toolbar */}
        <span className="w-2" />
        {which && (
          <Button variant="admin_secondary" className="h-8 px-3 text-xs" onClick={() => onOpen(c)} data-testid="contract-history-view">
            {t`View`}
          </Button>
        )}
        {actions || null}
      </div>
      {open && (
        <div className="flex flex-col gap-1 pl-7">
          {c.title && <div className="text-sm">{c.title}</div>}
          {/* its terms and dates, one per line */}
          <ul className="m-0 pl-5 list-disc marker:text-admin_neutral_2 text-sm text-admin_text_secondary flex flex-col gap-0.5 [&>li]:first-letter:uppercase">
            <li>{t("You receive %s%%", shareOf(c))}</li>
            <li>{t("Aldoo commission %s%% of what buyers pay for your products", c.commissionPercent)}</li>
            {c.deliveryToVendor !== false && <li>{t`the delivery fee is yours in full`}</li>}
            {/* the contract's clause 2а: how the commission on cash on delivery sales is collected */}
            {c.codCard ? <li>{t`commission on cash on delivery sales: taken from your online payouts, the rest charged monthly to your card (debited from your bank account if the card is declined)`}</li> : c.codDebits && <li>{t`commission on cash on delivery sales: taken from your online payouts, the rest debited monthly from your bank account`}</li>}
            {/* the contract in force says when its sales are paid in full, with the next payout's window */}
            <li>{statusOf(c) === "signed" ? scheduleText(t, c, locale) : t("paid on the %s of the month, %s days after the sale", c.payoutDay, c.holdDays)}</li>
            {Number(c.minPayout) > 0 && <li>{t("payouts from %s", money(Math.round(Number(c.minPayout) * 100), c.currency || "eur"))}</li>}
            {c.sentAt && <li>{t("Sent %s", dateOnly(c.sentAt, locale))}</li>}
            {c.signedAt && <li>{c.signature?.name ? t("Signed by %s on %s", c.signature.name, dateOnly(c.signedAt, locale)) : t("Signed %s", dateOnly(c.signedAt, locale))}</li>}
            {["signed", "superseded"].includes(c.status) && !c.correctedBy && from && <li>{c.inForceTo ? t("In force %s – %s", dateOnly(from, locale), dateOnly(c.inForceTo, locale)) : t("In force from %s", dateOnly(from, locale))}</li>}
              {c.replacesRef?.number && (
            <li>
              {c.reason === "correction"
                ? t("Correction: this contract corrects contract No. %s and replaces it from its start (%s)", c.replacesRef.number, dateOnly(c.replacesRef.effectiveFrom, locale))
                : t("New terms: this contract replaces contract No. %s from the day it is signed", c.replacesRef.number)}
            </li>
          )}
          {c.reasonText && <li className="italic">{t("Reason: %s", c.reasonText)}</li>}
          {signerNote && <li>{signerNote}</li>}
          </ul>
          {c.notes && c.source === "upload" && <div className="text-xs text-admin_text_secondary">{c.notes}</div>}
          {admin && c.correctionResult && (
            <div className="text-xs text-admin_text_secondary">
              Re-settled {c.correctionResult.orders} orders ({c.correctionResult.updated} unpaid rows changed)
              {c.correctionResult.adjustments?.length ? `; paid sales adjusted on the next payout: ${c.correctionResult.adjustments.map((a) => money(a.amount, a.currency)).join(", ")}` : "; no paid sales changed"}
            </div>
          )}
          {admin && c.uploadResult && (
            <div className="text-xs text-admin_text_secondary">
              Upload re-settled {c.uploadResult.orders} orders ({c.uploadResult.updated} unpaid rows changed); {c.uploadResult.paidRows} paid rows in its period left as paid
              {c.originalName ? ` · file “${c.originalName}”` : ""}
            </div>
          )}
        </div>
      )}
    </li>
  )
}

// a change per line, its own bullet or dash dropped (the list draws one)
const changeLines = (text) =>
  String(text || "")
    .split("\n")
    .map((line) => line.replace(/^\s*[-•*–]\s*/, "").trim())
    .filter(Boolean)

/**
 * What each new version of the contract changed, newest first: the admin's
 * summary given when it was issued (store/payouts/contracts `changes`), with
 * its number, why it was made and when it was sent and signed. Scrolls.
 */
function ChangesDialog({ list, open, onClose, locale, t }) {
  const versions = list.filter((c) => c.changes)
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={t`Changes`}
      variant="admin_compact"
      closeOnEscape
      closeOnClickOutside
      styleOverrides={{ container: "w-full max-w-[640px] max-h-[100dvh] md:max-h-[85vh] bg-admin_paper md:rounded-lg border border-admin_bordered shadow-lg overflow-y-auto" }}
    >
      <div className="flex flex-col gap-5 px-4 pb-4" data-testid="contract-changes">
        {versions.map((c) => (
          <section key={c._id} className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-semibold">{t("Contract No. %s", numberOf(c))}</span>
              {c.reason && <Chip className={c.reason === "correction" ? "bg-admin_warning/10 text-admin_warning" : "bg-admin_neutral_1 text-admin_text_secondary"}>{t(REASON[c.reason])}</Chip>}
              <span className="text-xs text-admin_text_secondary">
                {[c.sentAt && t("Sent %s", dateOnly(c.sentAt, locale)), c.signedAt && t("Signed %s", dateOnly(c.signedAt, locale))].filter(Boolean).join(" · ")}
              </span>
            </div>
            {c.reasonText && <div className="text-sm italic text-admin_text_secondary">{c.reasonText}</div>}
            <ul className="m-0 pl-5 list-disc marker:text-admin_neutral_2 text-sm flex flex-col gap-1">
              {changeLines(c.changes).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  )
}

/**
 * The history: newest first, each opened in a dialog with the PDF viewer.
 * `admin`: the withdrawn ones too, what each did to the sales, and the upload
 * of a contract signed before. `locale`: the dates the site language's way.
 */
export default function ContractHistory({ orgId, list = [], admin = false, onChanged, locale, actions, signerNote, adminActions = [] }) {
  const t = usePortalTexts(PAYOUTS)
  // the PDF open: the whole screen on a phone
  const pdfModal = usePdfModal()
  const [viewing, setViewing] = useState(null)
  // the file open, for the download in the dialog's title on a phone or a small tablet
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [changesOpen, setChangesOpen] = useState(false)
  // the contract that stands now (in force, or one waiting to be signed) shows; the earlier ones fold
  // under "See history". None standing: the newest shows. The list comes newest first.
  const [historyOpen, setHistoryOpen] = useState(false)
  const current = list.filter((c) => c.status === "signed" || c.status === "sent")
  const shown = current.length ? current : list.slice(0, 1)
  const earlier = list.filter((c) => !shown.includes(c))
  if (!list.length && !admin) return null
  const hasChanges = list.some((c) => c.changes)
  const viewingTitle = viewing ? `${t("Contract No. %s", numberOf(viewing))}${viewing.title ? ` · ${viewing.title}` : ""}` : ""
  return (
    <div className="flex flex-col gap-1 border-t border-admin_bordered pt-4 first:border-t-0 first:pt-0" data-testid="contract-history">
      <div className="flex flex-wrap items-center gap-3">
        {/* the contract that stands now heads the box; the earlier ones open from "See history" beside Changes */}
        <div className="text-base font-semibold flex-1">{t`Active contract`}</div>
        {earlier.length > 0 && (
          <Button variant="admin_secondary" className="h-8 px-3 text-xs" onClick={() => setHistoryOpen((v) => !v)} aria-expanded={historyOpen} data-testid="contract-history-more">
            {historyOpen ? t`Hide history` : t("See history (%s)", earlier.length)}
          </Button>
        )}
        {/* what each new version changed, in short */}
        {hasChanges && (
          <Button variant="admin_secondary" className="h-8 px-3 text-xs" onClick={() => setChangesOpen(true)} data-testid="contract-changes-open">
            {t`Changes`}
          </Button>
        )}
        {admin && <ActionsMenu label="Contract actions" testId="contract-admin-menu" items={[...adminActions, { label: "Upload a signed contract…", onClick: () => setUploading(true), testId: "contract-upload-signed" }]} />}
      </div>
      {list.length ? (
        <ul className="divide-y divide-admin_bordered">
          {(historyOpen ? [...shown, ...earlier] : shown).map((c) => (
            <HistoryRow key={c._id} contract={c} admin={admin} onOpen={setViewing} locale={locale} actions={actions?.(c)} signerNote={c.status === "sent" ? signerNote : null} />
          ))}
        </ul>
      ) : (
        <div className="text-sm text-admin_text_secondary py-2">No contracts yet.</div>
      )}
      <Modal
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        title={
          // on a phone or a small tablet the title is the download (the icon before the name, the whole
          // of it to tap): the viewer's toolbar has no room for it
          pdfModal.compact ? (
            <button
              type="button"
              className="flex items-center gap-2 max-w-full pr-10 text-left disabled:cursor-default"
              onClick={() => downloadPdf(file)}
              disabled={!file}
              title={t`Download`}
              data-testid="contract-download"
            >
              <ArrowDownTrayIcon className={`w-6 h-6 shrink-0 text-admin_text ${file ? "" : "opacity-40"}`} />
              <span className="min-w-0 truncate">{viewingTitle}</span>
            </button>
          ) : (
            viewingTitle
          )
        }
        variant={pdfModal.variant}
        closeOnEscape
        closeOnClickOutside
        styleOverrides={pdfModal.styleOverrides}
      >
        {viewing && <ContractPdf contractId={viewing._id} which={whichOf(viewing)} downloadable={!pdfModal.compact} onFile={setFile} height={pdfModal.height} />}
      </Modal>
      <ChangesDialog list={list} open={changesOpen} onClose={() => setChangesOpen(false)} locale={locale} t={t} />
      {admin && <UploadSignedDialog orgId={orgId} open={uploading} onClose={() => setUploading(false)} onDone={() => (setUploading(false), onChanged?.())} />}
    </div>
  )
}
